'use client'

import { createContext, useState, useEffect, ReactNode } from 'react'
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@/hooks/use-safe-local-storage'
import { useFailOpenContext } from '@/contexts/use-fail-open-context'
import { ERROR_IDS } from '@/lib/errors/constants'

export type DensityMode = 'compact' | 'expanded'

type DensityContextType = {
  density: DensityMode
  setDensity: (mode: DensityMode) => void
}

const DensityContext = createContext<DensityContextType | undefined>(undefined)

export { DensityContext }

/**
 * Safe default returned by `useDensity` when no provider is mounted
 * (#5860). Mirrors the provider's initial state — `expanded` density
 * with a no-op setter — so callers continue to render correctly while
 * the missing-provider log fires once for observability.
 */
const DENSITY_FAIL_OPEN_DEFAULT: DensityContextType = {
  density: 'expanded',
  setDensity: () => {},
}

export function DensityProvider({ children }: { children: ReactNode }) {
  // Issue #1712 - Initialize with 'expanded' as default (must be same on server and client)
  // Expanded shows more information; compact is available for dense views
  const [density, setDensityState] = useState<DensityMode>('expanded')

  // Load from localStorage after mount (client-only, no hydration mismatch)
  useEffect(() => {
    const saved = getLocalStorageItem('densityMode')
    if (saved === 'compact' || saved === 'expanded') {
      setDensityState(saved)
    }
  }, [])

  const setDensity = (mode: DensityMode) => {
    setDensityState(mode)
    // Save to localStorage (persists across pages and sessions)
    // Silently fails if localStorage blocked
    setLocalStorageItem('densityMode', mode)
  }

  return (
    <DensityContext value={{ density, setDensity }}>{children}</DensityContext>
  )
}

export function useDensity() {
  return useFailOpenContext(DensityContext, {
    hookName: 'useDensity',
    errorId: ERROR_IDS.CONTEXT_DENSITY_PROVIDER_MISSING,
    defaultValue: DENSITY_FAIL_OPEN_DEFAULT,
  })
}
